import { useTranslation } from 'react-i18next';
import type { Kolom, Sortering } from '@xxtract/tabelfilters';
import { ariaSort, SorteerbareKop } from '@/components/tabel';
import { Badge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import { computeAttentionReason } from '../attention';
import { formatLeadTime, formatOrderTimestamp } from '../format';
import type { OrderAggregate } from '../orders.api';
import { ORDERS_LIJST } from '../ordersLijst';
import { OrdersProgressBadge } from './OrdersProgressBadge';

/** Sorteerbare kolommen: sleutels = bronnamen (ORDERS_LIJST). */
const KOLOM = { orderNumber: 'orderNumber', createdAt: 'createdAt', lastEventAt: 'lastEventAt' } as const;
const STANDAARD = ORDERS_LIJST.standaardSortering?.({}) ?? [];
const kolom = (sleutel: string) => ORDERS_LIJST.kolommen.find((k) => k.sleutel === sleutel) as Kolom<never, never>;

interface OrdersTableProps {
  orders: OrderAggregate[];
  stalenessDays: number;
  locale: string;
  onOpenOrder: (orderNumber: string) => void;
  /** spec-tabelfilters story 11: sortering op één bronkolom; zonder `onSorteer` zijn de koppen gewone tekst. */
  sortering?: Sortering;
  onSorteer?: (sleutel: string) => void;
  /** De bron legt de volgorde op (zoekterm, tabblad "aandacht"): koppen zijn niet klikbaar. */
  vergrendeld?: boolean;
}

export function OrdersTable({ orders, stalenessDays, locale, onOpenOrder, sortering = [], onSorteer, vergrendeld }: OrdersTableProps) {
  const { t } = useTranslation();
  const emptyValue = t('common.notAvailable');
  const vast = vergrendeld || !onSorteer;
  const kop = (sleutel: string) => (
    <TableHead aria-sort={vast ? undefined : ariaSort(sortering, sleutel)}>
      <SorteerbareKop kolom={kolom(sleutel)} max={1} onKlik={() => onSorteer?.(sleutel)} sortering={sortering} standaard={STANDAARD} vergrendeld={vast} />
    </TableHead>
  );

  return (
    <Table data-testid="orders-table">
      <TableHeader>
        <TableRow>
          {kop(KOLOM.orderNumber)}
          <TableHead>{t('orders.table.customer')}</TableHead>
          <TableHead>{t('orders.table.services')}</TableHead>
          <TableHead className="text-right">{t('orders.table.productCount')}</TableHead>
          <TableHead>{t('orders.table.progressColumn')}</TableHead>
          {kop(KOLOM.createdAt)}
          {kop(KOLOM.lastEventAt)}
          <TableHead>{t('orders.table.leadTime')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orders.map((order) => {
          // fu-6/AC4: the stilstand-warning is shown inline next to "Laatste activiteit" (icon +
          // the timestamp text is the label, never color alone — NFR9); onhold-only rows are
          // surfaced via the Onhold tab and the Voortgang badge's b-onhold styling instead of a
          // separate leftmost pill column.
          const reason = computeAttentionReason(order, stalenessDays);
          const isStale = reason === 'stale' || reason === 'both';

          return (
            <TableRow
              key={order.orderNumber}
              data-testid={`order-row-${order.orderNumber}`}
              data-attention={reason ?? undefined}
              className="cursor-pointer"
              tabIndex={0}
              role="button"
              aria-label={t('orders.table.openOrder', { orderNumber: order.orderNumber })}
              onClick={() => onOpenOrder(order.orderNumber)}
              onKeyDown={(event) => { if (event.key === 'Enter') onOpenOrder(order.orderNumber); }}
            >
              <TableCell className="font-medium text-[var(--foreground)]">{order.orderNumber}</TableCell>
              <TableCell>
                <div className="flex flex-col">
                  <span className="text-[var(--foreground)]">{order.companyName ?? order.gln ?? emptyValue}</span>
                  {order.companyName != null && order.gln != null && (
                    <span className="text-xs text-[var(--foreground-muted)]">{order.gln}</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {order.serviceCodes.map((serviceCode) => (
                    <Badge key={serviceCode}>{serviceCode}</Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right tabular-nums">{order.productCount}</TableCell>
              <TableCell>
                <div className="flex flex-col gap-1">
                  {order.services.map((service) => (
                    <OrdersProgressBadge key={service.serviceCode} service={service} />
                  ))}
                </div>
              </TableCell>
              <TableCell>{order.createdAt != null ? formatOrderTimestamp(order.createdAt, locale) : emptyValue}</TableCell>
              <TableCell>
                {order.lastEventAt != null ? (
                  <span className="inline-flex items-center gap-1">
                    {isStale && (
                      <span
                        aria-label={t('orders.attention.stale')}
                        className="material-symbols-outlined text-[16px] text-[var(--warning-text)]"
                        title={t('orders.attention.tooltip')}
                      >
                        warning
                      </span>
                    )}
                    {formatOrderTimestamp(order.lastEventAt, locale)}
                  </span>
                ) : emptyValue}
              </TableCell>
              <TableCell>{order.leadTimeMs != null ? formatLeadTime(order.leadTimeMs, t) : emptyValue}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
